/**
 * Evidence System Types
 * 
 * Evidence as a first-class entity with attribute binding and quality tracking.
 * Part of Workstream 2: Evidence System
 */

// Evidence type classification
export type EvidenceType = 
  | 'data'                  // Quantitative data, metrics, analytics
  | 'research'              // Studies, reports, market research
  | 'expert_opinion'        // Subject matter expert input
  | 'customer_feedback'     // Customer interviews, surveys, support tickets
  | 'market_signal'         // Market trends, industry analysis
  | 'competitor_intel'      // Competitive analysis, feature comparisons
  | 'internal_observation'  // Internal observations, team feedback
  | 'assumption';           // Stated assumptions without backing evidence

// Evidence reliability spectrum
export type EvidenceReliability = 
  | 'verified'       // Multiple independent sources confirm, or authoritative source
  | 'corroborated'   // At least two sources agree
  | 'single_source'  // One credible source
  | 'anecdotal'      // Individual observation or opinion
  | 'speculative';   // Hypothesis or prediction without direct evidence

// Evidence access level
export type EvidenceAccessLevel = 
  | 'public'         // Publicly available information
  | 'internal'       // Internal to the organization
  | 'restricted';    // Limited access, confidential

// Strength of evidence support for a claim
export type EvidenceStrength = 'strong' | 'moderate' | 'suggestive';

/**
 * Binding between evidence and an attribute value
 */
export interface AttributeEvidenceBinding {
  id: string;
  evidenceId: string;
  attributeKey: string;
  valueSupported: unknown;      // The value this evidence supports
  strength: EvidenceStrength;
  notes?: string;
  createdAt: Date;
  createdBy: string;
}

/**
 * Evidence entity - first-class object attached to items
 */
export interface Evidence {
  id: string;
  itemId: string;
  
  // Classification
  type: EvidenceType;
  reliability: EvidenceReliability;
  accessLevel: EvidenceAccessLevel;
  
  // Source information
  source: string;               // Where did this come from?
  sourceType?: string;          // Type of source (e.g., "customer interview", "analytics dashboard")
  link?: string;                // URL or document reference
  
  // Content
  supportsClaim: string;        // What claim does this evidence support?
  summary: string;              // Brief description of the evidence
  rawContent?: string;          // Full text/data if available
  
  // Attribute bindings
  attributeBindings: AttributeEvidenceBinding[];
  
  // Metadata
  addedBy: string;
  addedAt: Date;
  
  // Verification
  verifiedBy?: string;
  verifiedAt?: Date;
  verificationNote?: string;
  
  // Expiration (for time-sensitive evidence)
  expiresAt?: Date;
  isExpired?: boolean;
  
  // Tags for categorization
  tags?: string[];
  
  // Workspace scoping
  workspaceId?: string;
}

/**
 * Evidence quality metrics for an item
 */
export interface EvidenceQualityMetrics {
  itemId: string;
  totalEvidence: number;
  
  // By type
  byType: Record<EvidenceType, number>;
  
  // By reliability
  byReliability: Record<EvidenceReliability, number>;
  
  // Quality indicators
  hasVerifiedEvidence: boolean;
  onlyAssumptions: boolean;
  hasExpiredEvidence: boolean;
  
  // Attribute coverage
  attributesWithEvidence: string[];
  attributesWithoutEvidence: string[];
  
  // Overall quality score (0-100)
  qualityScore: number;
  
  // Computed at
  computedAt: Date;
}

/**
 * Evidence requirement configuration for attributes
 */
export type EvidenceRequirement = 'required' | 'recommended' | 'optional';

/**
 * Attribute evidence configuration
 */
export interface AttributeEvidenceConfig {
  requirement: EvidenceRequirement;
  acceptableTypes?: EvidenceType[];
  minimumReliability?: EvidenceReliability;
  minimumCount?: number;
}

/**
 * Evidence summary for display
 */
export interface EvidenceSummary {
  id: string;
  type: EvidenceType;
  reliability: EvidenceReliability;
  source: string;
  summary: string;
  addedAt: Date;
  isVerified: boolean;
}

/**
 * Create evidence input
 */
export interface CreateEvidenceInput {
  itemId: string;
  type: EvidenceType;
  reliability: EvidenceReliability;
  accessLevel?: EvidenceAccessLevel;
  source: string;
  sourceType?: string;
  link?: string;
  supportsClaim: string;
  summary: string;
  rawContent?: string;
  expiresAt?: Date;
  tags?: string[];
  /** The value dimensions (attributes) this evidence bears on. */
  attributeBindings?: AttributeEvidenceBinding[];
}

/**
 * Update evidence input
 */
export interface UpdateEvidenceInput {
  type?: EvidenceType;
  reliability?: EvidenceReliability;
  accessLevel?: EvidenceAccessLevel;
  source?: string;
  sourceType?: string;
  link?: string;
  supportsClaim?: string;
  summary?: string;
  rawContent?: string;
  expiresAt?: Date;
  tags?: string[];
  /** The value dimensions (attributes) this evidence bears on; replaces the list. */
  attributeBindings?: AttributeEvidenceBinding[];
}

/**
 * Evidence filter options
 */
export interface EvidenceFilters {
  itemId?: string;
  types?: EvidenceType[];
  reliabilities?: EvidenceReliability[];
  isVerified?: boolean;
  isExpired?: boolean;
  addedBy?: string;
  attributeKey?: string;
  tags?: string[];
}

/**
 * Evidence statistics for dashboard
 */
export interface EvidenceStatistics {
  totalEvidence: number;
  totalItems: number;
  itemsWithEvidence: number;
  itemsWithoutEvidence: number;
  itemsWithOnlyAssumptions: number;
  
  byType: Record<EvidenceType, number>;
  byReliability: Record<EvidenceReliability, number>;
  
  averageEvidencePerItem: number;
  verifiedPercentage: number;
  
  // Top sources
  topSources: Array<{ source: string; count: number }>;
  
  // Recent additions
  recentlyAdded: number; // Last 7 days
}

// Helper functions

/**
 * Get display label for evidence type
 */
export function getEvidenceTypeLabel(type: EvidenceType): string {
  const labels: Record<EvidenceType, string> = {
    data: 'Data',
    research: 'Research',
    expert_opinion: 'Expert Opinion',
    customer_feedback: 'Customer Feedback',
    market_signal: 'Market Signal',
    competitor_intel: 'Competitor Intel',
    internal_observation: 'Internal Observation',
    assumption: 'Assumption',
  };
  return labels[type];
}

/**
 * Get display label for reliability level
 */
export function getReliabilityLabel(reliability: EvidenceReliability): string {
  const labels: Record<EvidenceReliability, string> = {
    verified: 'Verified',
    corroborated: 'Corroborated',
    single_source: 'Single Source',
    anecdotal: 'Anecdotal',
    speculative: 'Speculative',
  };
  return labels[reliability];
}

/**
 * Get numeric score for reliability (for sorting/comparison)
 */
export function getReliabilityScore(reliability: EvidenceReliability): number {
  const scores: Record<EvidenceReliability, number> = {
    verified: 5,
    corroborated: 4,
    single_source: 3,
    anecdotal: 2,
    speculative: 1,
  };
  return scores[reliability];
}

/**
 * Check if evidence meets minimum reliability requirement
 */
export function meetsReliabilityRequirement(
  evidence: Evidence,
  minimum: EvidenceReliability
): boolean {
  return getReliabilityScore(evidence.reliability) >= getReliabilityScore(minimum);
}

/**
 * Get icon for evidence type
 */
export function getEvidenceTypeIcon(type: EvidenceType): string {
  const icons: Record<EvidenceType, string> = {
    data: '📊',
    research: '📑',
    expert_opinion: '👤',
    customer_feedback: '💬',
    market_signal: '📈',
    competitor_intel: '🏢',
    internal_observation: '👁️',
    assumption: '⚠️',
  };
  return icons[type];
}

/**
 * Calculate evidence quality score
 */
export function calculateEvidenceQualityScore(evidence: Evidence[]): number {
  if (evidence.length === 0) return 0;
  
  let score = 0;
  
  for (const e of evidence) {
    // Base score from reliability
    score += getReliabilityScore(e.reliability) * 10;
    
    // Bonus for verified
    if (e.verifiedBy) score += 5;
    
    // Penalty for assumptions
    if (e.type === 'assumption') score -= 5;
    
    // Penalty for expired
    if (e.isExpired) score -= 10;
  }
  
  // Normalize to 0-100
  const maxPossibleScore = evidence.length * 55; // Max 50 per evidence + 5 for verified
  return Math.min(100, Math.max(0, Math.round((score / maxPossibleScore) * 100)));
}
